import { useEffect, useRef, useState } from 'react';

import { useAppTheme } from '@ui-library/tokens/themeContext';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { customerIoInApp } from '@services/customerIoInApp';
import useStores from '@stores/useStores';

const readFlag = () => experiment.isFeatureEnabled(FeatureFlagKeys.enableCustomerIoInApp);

/**
 * Wires Customer.io in-app messaging to the two things that change under it: the
 * app theme and the rollout flag.
 *
 * Theme — Gist renders each message in an iframe and resolves light/dark from the
 * document's CSS `color-scheme`, so the app has to publish it. `resolvedMode` is
 * the right input, as it already collapses the 'system' choice to light or dark.
 *
 * Flag — `boot()` otherwise only runs at login, so a flip to on would do nothing
 * until the next one, and a flip to off would not silence a booted session.
 */
export const useCustomerIoInApp = () => {
  const { resolvedMode } = useAppTheme();
  const { userStore } = useStores();

  const [isEnabled, setIsEnabled] = useState(readFlag);
  // null until the flag has been observed once, so the initial state is not
  // mistaken for a flip — `userStore` owns the boot at login.
  const previousEnabledRef = useRef<boolean | null>(null);

  // Deliberately not `useFeatureFlagObserver`: it subscribes once with the value
  // captured on first render, so it never reports a flip back to off.
  useEffect(() => {
    const handleChange = () => setIsEnabled(readFlag());

    handleChange();

    return experiment.subscribe(handleChange);
  }, []);

  useEffect(() => {
    const previous = previousEnabledRef.current;
    previousEnabledRef.current = isEnabled;

    if (previous === null || previous === isEnabled) {
      return;
    }

    if (isEnabled) {
      customerIoInApp.boot(userStore.email);
    } else {
      customerIoInApp.shutdown();
    }
  }, [isEnabled, userStore]);

  // Keyed on the flag too, so a flip publishes or clears the scheme without waiting
  // for a theme change.
  useEffect(() => {
    customerIoInApp.setColorScheme(resolvedMode);
  }, [resolvedMode, isEnabled]);
};
